import {
  ContactFormActionsContext,
  ContactFormDataContext,
  useContactFormActionsFromContext,
  useContactFormDataFromContext,
} from 'src/SharedCommonContext/ContactFormContext';
import ContactFormHeading from 'src/components/ContactFormComponents/ContactFormHeading';
import Row from 'src/components/Row';
import Col from 'src/components/Col';
import Input from 'src/components/Input';
import CheckBox from 'src/components/Checkbox/Checkbox';
import React, { useState } from 'react';
import Tile from 'src/components/Tile';
import Callout from 'src/components/InformationCallout/Callout';
import NewBadge from 'src/components/NewBadge/NewBadge';
import Select from 'react-select';
import { IContact } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import { toast } from 'react-toastify';

interface ContactBasicInfoProps {
  formType: string;
  setFormType: (type: string) => void;
  contacts: IContact[];
  contactDropdownHeader: string;
  showToggle: boolean;
  contactOptions: any[];
  selectedContactValue: any;
  pageUsedFrom: any;
}

const ContactBasicInfo = ({
  formType,
  setFormType,
  contacts,
  contactDropdownHeader,
  showToggle,
  contactOptions,
  selectedContactValue,
  pageUsedFrom,
}: ContactBasicInfoProps) => {
  const formOptions = ['New', 'Existing'];

  const { contactFormData, errors }: ContactFormDataContext =
    useContactFormDataFromContext();

  const {
    setContactFormData,
    clearError,
    initializeContactFormDataFromEdit,
    snapshotContactForm,
    restoreContactFormViaSnapshot,
  }: ContactFormActionsContext = useContactFormActionsFromContext();

  // Profile-picture editing was intentionally removed from the contact form.
  // There's no use case for editing another user's photo (users manage their
  // own from Settings), and — critically — the contact PUT (UpdateUser) assigns
  // `user.ProfileImage = model.ProfileImage` unconditionally, so any request
  // that omits/nulls profileImage wipes the stored image. We must always pass
  // the contact's existing profileImage through untouched (see
  // getProfileImageForPayload). Do NOT reintroduce a picture field here until
  // the API does a null-guarded partial update.
  // TODO(brittle-api): fragile API contract — UpdateUser should only overwrite
  // ProfileImage when a value is provided. Fix backend, then this is safe.
  const disableEmailField = (): boolean => {
    return Boolean(contactFormData.id || contactFormData.isSpark);
  };

  const checkBoxValue = (): boolean => {
    if (
      contactFormData.sendActivationEmail ||
      (contactFormData.status &&
        contactFormData.status === 'Invited' &&
        !contactFormData.designated) ||
      (contactFormData.status &&
        contactFormData.status === 'Active' &&
        !contactFormData.designated) ||
      (contactFormData.lastLoggedOn && !contactFormData.designated)
    ) {
      return true;
    } else {
      return false;
    }
  };

  const isActivationEmailDisabled: boolean =
    contactFormData.userRole === 'Home Owner' && contactFormData.designated;

  const handleFormTypeChange = (type: string) => {
    setFormType(type);

    if (type === 'New') {
      setContactFormData({
        id: null,
        firstname: '',
        lastname: '',
        email: '',
        phone: '',
        userRole: 'default',
        selectedImage: null,
        profileImage: '',
        old: false,
      });

      restoreContactFormViaSnapshot();
    } else {
      snapshotContactForm();

      setContactFormData({
        firstname: '',
        lastname: '',
        email: '',
        phone: '',
        userRole: 'default',
        old: true,
      });
    }
  };

  const handleSelectExistingContact = (selectedOption: any) => {
    if (!selectedOption) {
      setContactFormData({
        firstname: '',
        lastname: '',
        email: '',
        phone: '',
        userRole: 'default',
        old: true,
      });

      return;
    }

    const selectedContact = contacts?.find(
      (contact: any) => contact.id === selectedOption.value
    );

    if (selectedContact) {
      // Get the current property (the one we're adding this contact to)
      const currentPropertyContactIsBeingAddedTo =
        contactFormData.properties?.[0];
      const existingContactProperties = selectedContact.properties || [];

      // Merge: current property + existing properties (avoiding duplicates).
      let mergedProperties = existingContactProperties;

      // Handle case where we're adding a property developer to a specific property.
      if (currentPropertyContactIsBeingAddedTo) {
        const isAlreadyAssociated = existingContactProperties.some(
          (prop: any) =>
            (prop.propertyId || prop.id) ===
            (currentPropertyContactIsBeingAddedTo.propertyId ||
              currentPropertyContactIsBeingAddedTo.propertyId)
        );

        if (isAlreadyAssociated && pageUsedFrom === 'Properties') {
          toast.error(
            `${selectedContact.firstname} ${selectedContact.lastname} is already associated with ${currentPropertyContactIsBeingAddedTo.name}`,
            {
              autoClose: 5000,
              position: 'top-center',
            }
          );

          // Property already exists - move it to front, keep others in order.
          const otherProperties = existingContactProperties.filter(
            (prop: any) =>
              (prop.propertyId || prop.id) !==
              currentPropertyContactIsBeingAddedTo.propertyId
          );

          mergedProperties = [
            currentPropertyContactIsBeingAddedTo,
            ...otherProperties,
          ];
        } else {
          // New property - add to front
          mergedProperties = [
            currentPropertyContactIsBeingAddedTo,
            ...existingContactProperties,
          ];
        }
      } else {
        mergedProperties = existingContactProperties;
      }

      initializeContactFormDataFromEdit({
        id: selectedContact.id,
        firstname: selectedContact.firstname || '',
        lastname: selectedContact.lastname || '',
        email: selectedContact.email || '',
        phone: selectedContact.phone || '',
        userRole: selectedContact.roles?.[0]?.role?.name || 'default',
        profileImage: selectedContact.profileImage || '',
        address: selectedContact.address || {},
        status: selectedContact.status || '',
        isPrimaryContact: selectedContact.isPrimaryContact || false,
        sendActivationEmail: false,
        properties: mergedProperties,
        propertyId: selectedContact.propertyId || '',
        suiteId: selectedContact.suiteId || '',
        Suites: selectedContact.userSuites || [],
        serviceType: selectedContact.serviceType || '',
        servicesProvided: selectedContact.servicesProvided || [],
        designated: selectedContact.designated || false,
        lastLoggedOn: selectedContact.lastLoggedOn || '',
        isSpark: selectedContact.isSpark || false,
        old: true,
      });
    }
  };

  return (
    <>
      <ContactFormHeading>Contact Information</ContactFormHeading>
      {/* Only show toggle on Properties/Suites pages, not on Contacts page */}
      {showToggle && (
        <Row mdCenter style={{ marginBottom: '2rem' }}>
          {formOptions.map((type: string) => (
            <div key={type} style={{ padding: '0.5rem', flexDirection: 'row' }}>
              <Tile
                onClick={() => handleFormTypeChange(type)}
                active={formType === type}
                title={type}
                size="veryTiny"
                icon={undefined}
              />
            </div>
          ))}
        </Row>
      )}
      {/* Only show dropdown when toggle is visible and Existing is selected */}
      {showToggle && formType === 'Existing' && (
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ marginBottom: '1rem', fontWeight: 'bold' }}>
            {contactDropdownHeader.replace(/[()]/g, '')}
            <span style={{ color: 'red' }}>*</span>
          </div>
          <Select
            value={selectedContactValue}
            onChange={handleSelectExistingContact}
            options={contactOptions}
            placeholder={contactDropdownHeader}
            isClearable
          />
        </div>
      )}
      {/* Show form fields when: New is selected, or toggle is hidden (Contacts page), or editing existing contact */}
      {(formType === 'New' || !showToggle) && (
        <>
          <Row>
            <Col xs="12" md="6">
              <Input
                value={contactFormData.firstname}
                onChange={(e: any) =>
                  setContactFormData({ firstname: e.target.value })
                }
                label="First Name"
                placeholder="First Name"
                block
                required
                errors={errors.firstname}
              />
            </Col>
            <Col xs="12" md="6">
              <Input
                value={contactFormData.lastname}
                onChange={(e: any) =>
                  setContactFormData({ lastname: e.target.value })
                }
                label="Last Name"
                placeholder="Last Name"
                block
                required
                errors={errors.lastname}
              />
            </Col>
          </Row>
          <Input
            value={contactFormData.email}
            onChange={(e: any) => {
              clearError('email');
              setContactFormData({ email: e.target.value });
            }}
            label="Email"
            placeholder="Email"
            block
            required
            errors={errors.email}
            disabled={disableEmailField()}
          />
          {contactFormData.id && (
            <Callout
              variant="info"
              title={
                <>
                  Users can now change their own email address
                  <NewBadge spaced />
                </>
              }
              subtitle="Email changes are self-service — users can update their email directly from their Settings page without needing admin assistance."
            />
          )}
          <CheckBox
            value={checkBoxValue()}
            label="Send Activation Email"
            onChange={() =>
              setContactFormData({
                sendActivationEmail: !contactFormData.sendActivationEmail,
              })
            }
            disabled={isActivationEmailDisabled}
          />
          {isActivationEmailDisabled && (
            <div className="input__error" style={{ marginTop: '1rem' }}>
              Activation emails cannot be sent to homeowners that have been set
              as an inspection designate
            </div>
          )}
          <Input
            value={contactFormData.phone}
            onChange={(e: any) => {
              clearError('phone');
              setContactFormData({ phone: e.target.value });
            }}
            label="Phone Number"
            placeholder="Phone Number"
            block
            required
            errors={errors.phone}
          />
        </>
      )}
    </>
  );
};

export default ContactBasicInfo;
